import { api } from '@/utils/apiClient';
import { DEFAULT_HERO_SLIDES } from '@/components/figma-home/hero/heroSlides';

let publicSettingsCache = null;
let publicSettingsPromise = null;


export async function getPublicSettings(fetchOptions = {}) {
  const forceRefresh = Boolean(fetchOptions?.forceRefresh);
  if (!forceRefresh && publicSettingsCache) {
    return publicSettingsCache;
  }
  if (!forceRefresh && publicSettingsPromise) {
    return publicSettingsPromise;
  }

  const request = api.get('/settings/public', { cache: 'no-store', ...fetchOptions })
    .then((payload) => {
      publicSettingsCache = payload || {};
      return publicSettingsCache;
    })
    .catch(() => ({}))
    .finally(() => {
      publicSettingsPromise = null;
    });

  publicSettingsPromise = request;
  const result = await request;
  return result;
}

export function clearPublicSettingsCache() {
  publicSettingsCache = null;
  publicSettingsPromise = null;
}

const safeJsonParse = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const hasHeroSlideContent = (slide) =>
  Boolean(
    String(slide?.image || slide?.imageUrl || '').trim() ||
      String(slide?.title || '').trim() ||
      String(slide?.caption || slide?.description || '').trim() ||
      String(slide?.alt || '').trim(),
  );

export function getHomepageBanner1OverrideFromSettings(settings = {}) {
  return getHomepageBannerOverrideFromSettings(settings, 'homepage_banner_1');
}

export function getHomepageBanner2OverrideFromSettings(settings = {}) {
  return getHomepageBannerOverrideFromSettings(settings, 'homepage_banner_2');
}

export function getHomepageHeroSlidesFromSettings(settings = {}) {
  const rawMode = settings?.homepage_hero_mode;
  const mode = String(rawMode || '').trim().toLowerCase();
  const defaultSlides = [1, 2, 3]
    .map((index) => getHomepageHeroSlideFromSettings(settings, `homepage_hero_default_slide_${index}`))
    .filter(Boolean);
  const customSlides = [1, 2, 3, 4, 5, 6, 7]
    .map((index) => getHomepageHeroSlideFromSettings(settings, `homepage_hero_slide_${index}`))
    .filter(hasHeroSlideContent);

  if (mode === 'custom') {
    return customSlides.length ? customSlides : defaultSlides;
  }

  return defaultSlides.length ? defaultSlides : DEFAULT_HERO_SLIDES;
}

export function getHomepageHeroPresentationFromSettings(settings = {}) {
  return {
    mode: String(settings?.homepage_hero_mode || 'default').trim().toLowerCase() === 'custom' ? 'custom' : 'default',
    showChips: settings?.homepage_hero_show_chips !== false && settings?.homepage_hero_show_chips !== 'false',
    showDisplayBanner:
      settings?.homepage_hero_show_display_banner !== false &&
      settings?.homepage_hero_show_display_banner !== 'false',
    showOverlay:
      settings?.homepage_hero_show_overlay !== false &&
      settings?.homepage_hero_show_overlay !== 'false',
  };
}

function getHomepageBannerOverrideFromSettings(settings = {}, key = '') {
  const rawMode = settings?.homepage_banner_mode;
  const mode = String(rawMode || '').trim().toLowerCase();
  if (mode !== 'custom') return null;

  const raw = settings?.[key];
  const parsed = typeof raw === 'string' ? safeJsonParse(raw) : raw;
  if (!parsed || typeof parsed !== 'object') return null;

  const banner = {
    eyebrow: typeof parsed.eyebrow === 'string' ? parsed.eyebrow : '',
    title: typeof parsed.title === 'string' ? parsed.title : '',
    description: typeof parsed.description === 'string' ? parsed.description : '',
    ctaLabel: typeof parsed.ctaLabel === 'string' ? parsed.ctaLabel : '',
    ctaHref: typeof parsed.ctaHref === 'string' ? parsed.ctaHref : '',
    imageUrl: typeof parsed.imageUrl === 'string' ? parsed.imageUrl : '',
    tint: typeof parsed.tint === 'string' ? parsed.tint : '',
    alt: typeof parsed.alt === 'string' ? parsed.alt : '',
    clickEnabled: parsed.clickEnabled !== false,
  };

  return { mode: 'custom', banner };
}

function getHomepageHeroSlideFromSettings(settings = {}, key = '') {
  const raw = settings?.[key];
  const parsed = typeof raw === 'string' ? safeJsonParse(raw) : raw;
  if (!parsed || typeof parsed !== 'object') return null;

  return {
    image: typeof parsed.image === 'string' ? parsed.image : typeof parsed.imageUrl === 'string' ? parsed.imageUrl : '',
    title: typeof parsed.title === 'string' ? parsed.title : '',
    caption: typeof parsed.caption === 'string' ? parsed.caption : typeof parsed.description === 'string' ? parsed.description : '',
    alt: typeof parsed.alt === 'string' ? parsed.alt : '',
  };
}

export function getFlashDealNowFromSettings(settings = {}) {
  const raw = settings?.flash_deal_today;
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function getFlashDealRangeFromSettings(settings = {}) {
  const toIso = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };

  const maxDaysRaw = settings?.flash_deal_max_days ?? settings?.flash_deal_range_end;
  const maxDaysNum = Number(maxDaysRaw);
  const maxDays = Number.isFinite(maxDaysNum) ? Math.max(1, Math.trunc(maxDaysNum)) : null;

  const start = new Date();
  const end = maxDays ? new Date(start.getTime()) : null;
  if (end) end.setDate(end.getDate() + maxDays);

  return {
    start: start.toISOString(),
    end: end ? end.toISOString() : toIso(settings?.flash_deal_range_end),
  };
}

export function getOrderPendingPaymentTimeoutMinutesFromSettings(settings = {}) {
  const raw = Number(settings?.order_stale_pending_payment_ttl_minutes);
  if (Number.isFinite(raw) && raw >= 5) {
    return Math.floor(raw);
  }
  return 30;
}

export function formatOrderPendingPaymentTimeoutMessage(settings = {}) {
  const minutes = getOrderPendingPaymentTimeoutMinutesFromSettings(settings);
  const unit = minutes === 1 ? 'minute' : 'minutes';
  return 'This order will auto-cancel in about ' + minutes + ' ' + unit + ' if payment is not completed.';
}
